import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PlaneTakeoff, Smartphone } from "lucide-react";
import OtpLogin from "@/components/site/OtpLogin";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function GoogleSignInButton() {
  const startGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={startGoogle}
      data-testid="google-signin"
      className="w-full py-3.5 rounded-full flex items-center justify-center gap-3 border border-slate-300 dark:border-white/20 bg-white dark:bg-transparent text-slate-800 dark:text-white text-sm font-semibold hover:bg-slate-50 dark:hover:bg-white/5"
    >
      <svg width="18" height="18" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      </svg>
      Continue with Google
    </button>
  );
}

function OrDivider() {
  return (
    <div className="flex items-center gap-3 my-2">
      <div className="flex-1 h-px bg-slate-200 dark:bg-white/10"/>
      <div className="text-xs uppercase tracking-widest text-slate-400">or</div>
      <div className="flex-1 h-px bg-slate-200 dark:bg-white/10"/>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState("email"); // email | otp
  const [f, setF] = useState({ email: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { const u = await login(f.email, f.password); nav(u.role === "admin" ? "/admin" : (loc.state?.from || "/")); } catch {}
  };
  if (mode === "otp") return <AuthShell title="Sign in with mobile" sub="We'll send a 6-digit code to your phone.">
    <OtpLogin onCancel={() => setMode("email")} />
  </AuthShell>;
  return <AuthShell title="Welcome back" sub="Sign in to manage bookings, wishlist and more.">
    <GoogleSignInButton/>
    <button type="button" onClick={() => setMode("otp")} data-testid="use-otp-btn"
      className="w-full py-3.5 rounded-full flex items-center justify-center gap-3 border border-slate-300 dark:border-white/20 bg-white dark:bg-transparent text-slate-800 dark:text-white text-sm font-semibold hover:bg-slate-50 dark:hover:bg-white/5">
      <Smartphone className="w-4 h-4 text-[#D4AF37]"/> Continue with Mobile OTP
    </button>
    <OrDivider/>
    <form onSubmit={submit} data-testid="login-form" className="space-y-4">
      <F label="Email" type="email" value={f.email} onChange={e=>setF({...f,email:e.target.value})} data-testid="login-email"/>
      <F label="Password" type="password" value={f.password} onChange={e=>setF({...f,password:e.target.value})} data-testid="login-password"/>
      <button data-testid="login-submit" className="w-full py-3.5 rounded-full btn-gold text-sm">Sign In</button>
      <div className="text-center text-sm text-slate-500">New here? <Link to="/register" className="text-[#D4AF37] font-semibold">Create account</Link></div>
    </form>
  </AuthShell>;
}
export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name:"", email: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await register(f.name, f.email, f.password); nav("/"); } catch {}
  };
  return <AuthShell title="Join Your Trips" sub="Save wishlists, track bookings and unlock member deals.">
    <GoogleSignInButton/>
    <OrDivider/>
    <form onSubmit={submit} data-testid="register-form" className="space-y-4">
      <F label="Full name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="register-name"/>
      <F label="Email" type="email" value={f.email} onChange={e=>setF({...f,email:e.target.value})} data-testid="register-email"/>
      <F label="Password" type="password" value={f.password} onChange={e=>setF({...f,password:e.target.value})} data-testid="register-password"/>
      <button data-testid="register-submit" className="w-full py-3.5 rounded-full btn-gold text-sm">Create Account</button>
      <div className="text-center text-sm text-slate-500">Already a member? <Link to="/login" className="text-[#D4AF37] font-semibold">Sign in</Link></div>
    </form>
  </AuthShell>;
}
function AuthShell({ title, sub, children }) {
  return (
    <div className="pt-32 pb-24 min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      <div className="max-w-md mx-auto px-6">
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 rounded-2xl btn-gold items-center justify-center"><PlaneTakeoff className="w-6 h-6"/></div>
          <h1 className="mt-5 text-4xl font-bold text-blue-950 dark:text-white">{title}</h1>
          <p className="mt-2 text-slate-500">{sub}</p>
        </div>
        <div className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 shadow-xl space-y-4">
          {children}
        </div>
      </div>
    </div>
  );
}
function F({ label, ...rest }) {
  return <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
    <input required {...rest} className="mt-1 w-full rounded-2xl border border-slate-200 dark:border-white/10 bg-transparent px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"/></label>;
}
